import { Box, Button, Heading, HStack, IconButton, Link, List, ListItem, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { baseUrl, useGetTracerQuery } from '@/app/services/nucleus';
import { createColumnHelper } from '@tanstack/react-table';
import AlertComponent from '../common/AlertComponent';
import DataTable from '../common/table/DataTable';
import { LiaLongArrowAltRightSolid } from 'react-icons/lia';
import { LuArrowRightToLine, LuArrowRightFromLine } from 'react-icons/lu';
import TruncatedTextComponent from '../common/TruncatedTextComponent';
import { isArrayValue } from '@/utils/helpers';
import { FiChevronRight } from 'react-icons/fi';

const ViewTracer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const columnHelper = createColumnHelper();

  const { data: tracerData, isLoading, isError, error: tracerError } = useGetTracerQuery({ trace_id: id });

  const createDateColumn = (columnHelper, id, header) => {
    return columnHelper.accessor(id, {
      header: header,
      cell: (info) => {
        const date = new Date(info.getValue());
        return date.toLocaleString();
      },
      size: 10,
      meta: { widthStrategy: '%' },
    });
  };

  const tracerDataColumn = useMemo(
    () => [
      columnHelper.accessor('error_count', {
        header: 'Number of Errors',
        enableSorting: false,
        size: 15,
        cell: (info) => info.getValue(),
        meta: { widthStrategy: '%' },
      }),
      createDateColumn(columnHelper, 'inserted_at', 'Inserted At'),
      createDateColumn(columnHelper, 'updated_at', 'Updated At'),
      columnHelper.accessor('', {
        header: 'Action',
        cell: ({ row }) => {
          return (
            <Link aria-label="download file" href={`${baseUrl}v1/streams/tracers/${row.original.trace_id}/export`} isExternal>
              <Button size="sm" variant="outline">
                Export Tracer
              </Button>
            </Link>
          );
        },
        size: 10,
        meta: { widthStrategy: '%' },
      }),
    ],
    [],
  );

  const handleViewLog = (pk) => {
    let query = `logs/${pk}`;
    if (location.search) {
      query = `${query}${location.search}`;
    }
    navigate(query, { state: { from: location } });
  };

  const getRowColor = (errorCount) => {
    return errorCount > 0 ? 'red.50' : 'white';
  };

  const onTableLoaded = (table) => {
    if (isArrayValue(tracerData)) {
      tracerData.forEach((trace, idx) => {
        const color = getRowColor(trace.error_count);
        table.options.meta?.addRowColor(idx, color);
      });
    }
  };

  return (
    <Box data-testid="pact-tracer-logs">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        View Tracer
      </Heading>

      {isError ? (
        <AlertComponent status="error" title="Error fetching tracer data" description={tracerError?.data?.detail} />
      ) : (
        <Box py={5} width={{ base: '100%' }}>
          <DataTable
            bordered={true}
            showScrollbar={false}
            columns={tracerDataColumn}
            data={tracerData}
            loading={tracerData?.length > 1}
            paginate={false}
            onLoaded={onTableLoaded}
            loadingDependencies={[isLoading]}
          />
        </Box>
      )}

      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Logs
      </Heading>

      <List spacing={2} mt={4}>
        {isArrayValue(tracerData) &&
          tracerData[0].logs.map((log, idx) => (
            <ListItem
              key={`${idx}`}
              border="1px"
              borderColor="gray.200"
              py={4}
              pl={4}
              pr={0}
              pos={'relative'}
              borderRadius="md"
              bg={log.error_message ? 'red.50' : log.handled ? 'green.100' : 'white'}
            >
              <HStack mr={10} justifyContent={'space-between'}>
                <Box width="100%">
                  <HStack justify="space-between">
                    <HStack>
                      {log.search_field ? (
                        <LuArrowRightToLine size={20} />
                      ) : log.consumer_group && log.consumer_name ? (
                        <LuArrowRightFromLine size={20} />
                      ) : null}
                      <Text fontWeight="bold">{log.stream_name}</Text>
                    </HStack>
                    <Text fontSize="sm">{log.message_id}</Text>
                  </HStack>
                  <HStack justify="space-between" alignItems={'start'}>
                    <Box fontSize="xs" color="gray.500" width="50%">
                      {log.search_field ? (
                        <TruncatedTextComponent key={log.search_field} title="Search Field" text={log.search_field} length={80} />
                      ) : (
                        <Text>
                          {log.consumer_group} - {log.consumer_name}
                        </Text>
                      )}
                    </Box>
                    <HStack fontSize="xs" color="gray.500">
                      <Text>Started {new Date(log.started).toLocaleString()}</Text>
                      <LiaLongArrowAltRightSolid />
                      <Text>Completed {new Date(log.completed_at).toLocaleString()}</Text>
                    </HStack>
                  </HStack>
                </Box>
                <IconButton
                  size={'sm'}
                  position={'absolute'}
                  borderLeftRadius={0}
                  right={0}
                  height={'100%'}
                  variant={'ghost'}
                  _hover={{ bg: log.error_message ? 'red.200' : log.handled ? 'green.200' : 'gray.50' }}
                  aria-label={'view logs'}
                  onClick={() => handleViewLog(log.id)}
                  icon={<FiChevronRight />}
                />
              </HStack>
            </ListItem>
          ))}
      </List>
    </Box>
  );
};

export default ViewTracer;
